import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { WelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/welcomeGift';
import { isDateBefore, isDateSameOrAfter } from '@gamiphy/utils/date';
import { setSessionStorageItem } from '@gamiphy/utils/storage';
import { sameUrl } from '@gamiphy/utils/url';
import {
  PopupConditionsDto, PopupConditionsDtoFieldEnum, PopupConditionsDtoOperationEnum, PopupDto, PopupTriggerDetailsDtoDisplayFrequencyEnum,
  PopupTriggerUrlDtoDisplayConditionEnum,
} from '@coretava/api/core-service';
import { ReferralsStore } from '../refferals';

// Get the values that we need to check against based on the field in the condition
const getValueToCheck = (field: PopupConditionsDtoFieldEnum) => {
  switch (field) {
    case PopupConditionsDtoFieldEnum.UserId:
      return UserStore.user?.id;
    case PopupConditionsDtoFieldEnum.WelcomeGiftEarned:
      return WelcomeGiftStore.unlocked;
    case PopupConditionsDtoFieldEnum.Location:
      return window.location.href;
    case PopupConditionsDtoFieldEnum.ReferralEnabled:
      return ReferralsStore.appReferral?.enabled;
    default:
      return null;
  }
};

// Check that every equal condition is met
const checkEqualConditions = (equalConditions: PopupConditionsDto[]) => equalConditions.every((condition) => getValueToCheck(condition.field) === condition.value);
// Check that every exist condition is met
const checkExistConditions = (existConditions: PopupConditionsDto[]) => existConditions.every((condition) => getValueToCheck(condition.field));
// Check that every NOT exist condition is met
const checkNotExistConditions = (notExistConditions: PopupConditionsDto[]) => notExistConditions.every((condition) => !getValueToCheck(condition.field));
// Check if every contains condition is met
const checkContainConditions = (containsConditions: PopupConditionsDto[]) => containsConditions.every((condition) => {
  const currentUrl = getValueToCheck(condition.field);
  return !(typeof currentUrl === 'string' && typeof condition.value === 'string' && !currentUrl.includes(condition.value));
});

/**
 * Check if the popup is live or not
 * @param popup Object
 * @returns boolean
 */
const isPopupLive = (popup?: PopupDto): boolean => (popup?.duration?.startDate ? isDateBefore(new Date(popup?.duration?.startDate), new Date()) : true);

/**
 * Check if the popup is Expired or not
 * @param popup popup Object
 * @returns boolean
 */
const isPopupExpired = (popup?: PopupDto): boolean => (popup?.duration?.endDate ? isDateSameOrAfter(new Date(popup?.duration?.endDate), new Date()) : true);

export const shouldShowPopup = (popup?: PopupDto): boolean => {
  // Check if there is a popup
  if (!popup) {
    return false;
  }
  // Check if the popup is live
  if (!isPopupLive(popup)) {
    return false;
  }
  // Check if the popup is Expired
  if (!isPopupExpired(popup)) {
    return false;
  }
  // check if the popup should open in current page
  if (popup.triggerDetails.targetingURL?.url) {
    switch (popup.triggerDetails.targetingURL?.displayCondition) {
      case PopupTriggerUrlDtoDisplayConditionEnum.Contains:
        if (!window.location.href.includes(popup.triggerDetails.targetingURL.url)) {
          return false;
        }
        break;
      case PopupTriggerUrlDtoDisplayConditionEnum.Equal:
        if (!sameUrl(popup.triggerDetails.targetingURL.url, window.location.href)) {
          return false;
        }
        break;
      case PopupTriggerUrlDtoDisplayConditionEnum.Exclude:
        if (sameUrl(popup.triggerDetails.targetingURL.url, window.location.href)) {
          return false;
        }
        break;
      default:
    }
  }

  // If there is a popup, check if it has any conditions
  if (popup.conditions && popup.conditions.length > 0) {
    // Separate conditions based on their operation
    const equalConditions = popup.conditions?.filter((condition) => condition.operation === PopupConditionsDtoOperationEnum.Equals);
    const existConditions = popup.conditions?.filter((condition) => condition.operation === PopupConditionsDtoOperationEnum.Exists);
    const notExistConditions = popup.conditions?.filter((condition) => condition.operation === PopupConditionsDtoOperationEnum.NotExists);
    const containConditions = popup.conditions?.filter((condition) => condition.operation === PopupConditionsDtoOperationEnum.Contains);
    // Check each condition array against the expected values
    if (checkEqualConditions(equalConditions) &&
        checkExistConditions(existConditions) &&
        checkNotExistConditions(notExistConditions) &&
        checkContainConditions(containConditions)) {
      // If all conditions pass, we return true (popup is shown)
      return true;
    }
    // If the conditions don't pass, we return false (popup is not shown)
    return false;
  }
  // If no conditions exist, we return true (popup is shown)
  return true;
};

export const setPopupSessionStorage = (popup?: PopupDto): void => {
  if (popup) {
    if (popup.triggerDetails.displayFrequency !== PopupTriggerDetailsDtoDisplayFrequencyEnum.Always) {
      setSessionStorageItem(`popup_${popup.id}`, 'true');
    }
  }
};
